import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Package, MapPin, Calendar, Clock, DollarSign, Truck, User,
  CheckCircle, XCircle, AlertCircle, Loader2, Eye, Navigation, FileText,
  Building2, Phone, Mail, Star, Check, X, FileSearch
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { useClientRequests } from '@/hooks/useClientRequests';
import { useClientOffers } from '@/hooks/useClientOffers';
import { getClientSidebarLinks } from '@/config/clientSidebarLinks';
import { clientAPI } from '@/services/api';
import { useConstantsContext } from '@/contexts/ConstantsContext';
import { constantsUtils } from '@/hooks/useConstants';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ClientRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { constants } = useConstantsContext();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [offerDetailDialogOpen, setOfferDetailDialogOpen] = useState(false);
  const [acceptOfferDialogOpen, setAcceptOfferDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [finalAmount, setFinalAmount] = useState('');

  const sidebarLinks = getClientSidebarLinks();

  // Charger la demande
  useEffect(() => {
    const fetchRequest = async () => {
      try {
        setLoading(true);
        const response = await clientAPI.getRequestById(id);
        setRequest(response.data);
      } catch (err) {
        console.error('Erreur lors du chargement de la demande:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger la demande.',
          variant: 'destructive',
        });
        navigate('/client/operations');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRequest();
    }
  }, [id, navigate]);

  // Charger les offres pour cette demande
  useEffect(() => {
    const fetchOffers = async () => {
      if (!id) return;
      try {
        setOffersLoading(true);
        const response = await clientAPI.getQuotes(id);
        setOffers(response.data.results || response.data || []);
      } catch (err) {
        console.error('Erreur lors du chargement des offres:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les offres.',
          variant: 'destructive',
        });
      } finally {
        setOffersLoading(false);
      }
    };

    if (id && request) {
      fetchOffers();
    }
  }, [id, request]);

  // Fonction pour publier la demande
  const handlePublishRequest = async () => {
    if (!request || !id) return;

    try {
      setPublishing(true);
      await clientAPI.publishRequest(id);

      toast({
        title: 'Demande publiée',
        description: 'Votre demande a été publiée et est maintenant visible par les transporteurs.',
        variant: 'success',
      });

      // Recharger la demande pour mettre à jour le statut
      const response = await clientAPI.getRequestById(id);
      setRequest(response.data);
    } catch (error) {
      console.error('Erreur lors de la publication:', error);
      const errorMessage = error.response?.data?.detail
        || error.response?.data?.message
        || 'Une erreur est survenue lors de la publication de la demande.';

      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'DRAFT': { label: 'Brouillon', color: 'bg-gray-500' },
      'PUBLISHED': { label: 'Publiée', color: 'bg-blue-500' },
      'ASSIGNED': { label: 'Assignée', color: 'bg-green-500' },
      'IN_TRANSIT': { label: 'En transit', color: 'bg-purple-500' },
      'COMPLETED': { label: 'Terminée', color: 'bg-green-600' },
      'CANCELLED': { label: 'Annulée', color: 'bg-red-500' },
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyConfig = {
      'NORMAL': { label: 'Normal', color: 'bg-blue-500' },
      'URGENT': { label: 'Urgent', color: 'bg-red-500' },
    };
    const config = urgencyConfig[urgency] || { label: urgency, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getPaymentTermsBadge = (terms) => {
    const termsConfig = {
      'PREPAID': { label: 'Prépaiement', color: 'bg-green-500' },
      'COD': { label: 'Paiement à la livraison', color: 'bg-yellow-500' },
      'POSTPAID': { label: 'Paiement différé', color: 'bg-blue-500' },
    };
    const config = termsConfig[terms] || { label: terms, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getOfferStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { label: 'En attente', color: 'bg-yellow-500', icon: Clock },
      'ACCEPTED': { label: 'Acceptée', color: 'bg-green-500', icon: CheckCircle },
      'REJECTED': { label: 'Rejetée', color: 'bg-red-500', icon: XCircle },
      'EXPIRED': { label: 'Expirée', color: 'bg-gray-500', icon: AlertCircle },
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500', icon: AlertCircle };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    // Afficher le nombre complet avec séparateurs de milliers
    return `${Number(amount).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} GNF`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleAcceptOffer = (offer) => {
    setSelectedOffer(offer);
    setFinalAmount(offer.price?.toString() || '');
    setAcceptOfferDialogOpen(true);
  };

  const confirmAcceptOffer = async () => {
    if (!selectedOffer || !finalAmount) {
      toast({
        title: 'Erreur',
        description: 'Veuillez saisir le montant final.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Accepter l'offre avec le montant final
      const result = await clientAPI.acceptQuote(selectedOffer.id, parseFloat(finalAmount));

      // Si l'API accepteQuote ne prend pas le montant, on peut avoir besoin d'une autre méthode
      // Pour l'instant, on utilise acceptQuote

      if (result.data) {
        toast({
          title: 'Offre acceptée',
          description: `L'offre a été acceptée avec un montant de ${formatCurrency(parseFloat(finalAmount))}.`,
        });
        setAcceptOfferDialogOpen(false);
        setSelectedOffer(null);
        setFinalAmount('');

        // Recharger les offres et la demande
        const [offersResponse, requestResponse] = await Promise.all([
          clientAPI.getQuotes(id),
          clientAPI.getRequestById(id)
        ]);
        setOffers(offersResponse.data.results || offersResponse.data || []);
        setRequest(requestResponse.data);
      }
    } catch (err) {
      console.error('Erreur lors de l\'acceptation de l\'offre:', err);

      // Extraire le message d'erreur de différents champs possibles
      const errorData = err.response?.data || {};
      let errorMessage = errorData.detail || errorData.message || errorData.error;

      // Si c'est une ValidationError, le message peut être dans un champ spécifique
      if (!errorMessage && typeof errorData === 'object') {
        // Chercher dans les champs de validation (driver, vehicle, etc.)
        const validationFields = ['driver', 'vehicle', 'non_field_errors'];
        for (const field of validationFields) {
          if (errorData[field]) {
            const fieldError = Array.isArray(errorData[field])
              ? errorData[field][0]
              : errorData[field];
            if (fieldError) {
              errorMessage = fieldError;
              break;
            }
          }
        }
      }

      toast({
        title: 'Erreur',
        description: errorMessage || 'Erreur lors de l\'acceptation de l\'offre.',
        variant: 'destructive',
      });
    }
  };

  const handleViewOfferDetails = (offer) => {
    setSelectedOffer(offer);
    setOfferDetailDialogOpen(true);
  };

  // Fonction pour compter les offres visibles pour cette demande
  const getVisibleOffersCount = () => {
    return offers.filter(o => o.visible_to_client === true).length;
  };

  if (loading) {
    return (
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (!request) {
    return (
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Demande introuvable</h3>
            <p className="text-muted-foreground mb-4">La demande demandée n'existe pas ou a été supprimée.</p>
            <Button onClick={() => navigate('/client/operations')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour aux demandes
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Détails de la demande #{request.id.slice(-8)} - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/client/operations')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux demandes
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Demande #{request.id.slice(-8)}</h1>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(request.status)}
                {getUrgencyBadge(request.urgency_level)}
                {getPaymentTermsBadge(request.payment_terms)}
                {getVisibleOffersCount() > 0 && (
                  <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                    {getVisibleOffersCount()} {getVisibleOffersCount() === 1 ? 'offre' : 'offres'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Informations de la demande */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle>Informations de la demande</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Lieu de départ</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium">
                        {request.from_location_detail?.city || request.from_location_detail?.label || request.from_location?.city || request.from_location?.label || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Lieu d'arrivée</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium">
                        {request.to_location_detail?.city || request.to_location_detail?.label || request.to_location?.city || request.to_location?.label || 'N/A'}
                      </span>
                    </div>
                  </div>
                  {request.preferred_pickup_date && (
                    <div>
                      <Label className="text-muted-foreground">Date d'enlèvement prévue</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4" />
                        <span className="font-medium">{formatDate(request.preferred_pickup_date)}</span>
                      </div>
                    </div>
                  )}
                  {request.preferred_delivery_date && (
                    <div>
                      <Label className="text-muted-foreground">Date de livraison prévue</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4" />
                        <span className="font-medium">{formatDate(request.preferred_delivery_date)}</span>
                      </div>
                    </div>
                  )}
                  {request.declared_value && (
                    <div>
                      <Label className="text-muted-foreground">Valeur déclarée</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-medium">{formatCurrency(request.declared_value)}</span>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-muted-foreground">Date de création</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">{formatDate(request.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Nouveaux champs métier */}
                {(request.handling_responsibility || request.goods_type || request.preferred_vehicle_type_detail || request.estimated_weight_kg || request.estimated_volume_m3) && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Informations de transport</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {request.handling_responsibility && (
                          <div>
                            <Label className="text-muted-foreground">Manutention</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Package className="w-4 h-4" />
                              <span className="font-medium">
                                {constantsUtils.getLabel(constants?.handling_responsibility, request.handling_responsibility) || request.handling_responsibility}
                              </span>
                            </div>
                          </div>
                        )}
                        {request.goods_type && (
                          <div>
                            <Label className="text-muted-foreground">Type de marchandise</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Package className="w-4 h-4" />
                              <span className="font-medium">
                                {constantsUtils.getLabel(constants?.transport_domains, request.goods_type) || request.goods_type}
                              </span>
                            </div>
                          </div>
                        )}
                        {request.preferred_vehicle_type_detail && (
                          <div>
                            <Label className="text-muted-foreground">Type de véhicule souhaité</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Truck className="w-4 h-4" />
                              <span className="font-medium">
                                {request.preferred_vehicle_type_detail.label || request.preferred_vehicle_type_detail.code || 'N/A'}
                              </span>
                            </div>
                          </div>
                        )}
                        {request.estimated_weight_kg && (
                          <div>
                            <Label className="text-muted-foreground">Poids estimatif</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Package className="w-4 h-4" />
                              <span className="font-medium">{parseFloat(request.estimated_weight_kg).toLocaleString('fr-FR')} kg</span>
                            </div>
                          </div>
                        )}
                        {request.estimated_volume_m3 && (
                          <div>
                            <Label className="text-muted-foreground">Volume estimatif</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Package className="w-4 h-4" />
                              <span className="font-medium">{parseFloat(request.estimated_volume_m3).toLocaleString('fr-FR')} m³</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Offres reçues */}
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle>Offres reçues ({offers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {offersLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : offers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileSearch className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucune offre reçue pour cette demande.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {offers.map((offer) => {
                      const vehicle = offer.vehicle_detail || offer.vehicle;
                      return (
                        <Card key={offer.id} className="border-border bg-primary-700/20 hover:border-secondary transition-colors">
                          <CardContent className="p-6">
                            <div className="flex flex-col gap-6">
                              {/* En-tête simplifiée */}
                              <div className="space-y-3 pb-4 border-b border-border">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <h3 className="text-lg font-bold text-foreground">Offre #{offer.id.slice(-8)}</h3>
                                  {getOfferStatusBadge(offer.status)}
                                </div>
                                <div className="flex items-baseline gap-3">
                                  <div className="text-3xl font-bold text-secondary">
                                    {formatCurrency(offer.admin_display_price || offer.price)}
                                  </div>
                                  <div className="text-sm text-muted-foreground">Prix de l'offre</div>
                                </div>
                                {offer.transporter_detail && (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Truck className="w-4 h-4" />
                                    <span className="truncate">
                                      {offer.transporter_detail.organisation_name ||
                                       `${offer.transporter_detail.user_detail?.first_name || ''} ${offer.transporter_detail.user_detail?.last_name || ''}`.trim() ||
                                       'N/A'}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Actions */}
                              {offer.status === 'PENDING' && (
                                <div className="pt-4 border-t border-border">
                                  <Button
                                    onClick={() => handleAcceptOffer(offer)}
                                    size="default"
                                    className="bg-green-600 hover:bg-green-700 w-full text-sm py-3"
                                  >
                                    <Check className="w-4 h-4 mr-2" />
                                    Valider l'offre
                                  </Button>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Actions rapides */}
          <div className="space-y-6">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {request.status === 'DRAFT' && (
                  <Button
                    className="w-full"
                    onClick={handlePublishRequest}
                    disabled={publishing}
                  >
                    {publishing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Publication en cours...
                      </>
                    ) : (
                      <>
                        <Package className="w-4 h-4 mr-2" />
                        Publier la demande
                      </>
                    )}
                  </Button>
                )}
                {request.status === 'ASSIGNED' || request.status === 'IN_TRANSIT' && (
                  <Button variant="outline" className="w-full" onClick={() => navigate('/client/operations?tab=tracking')}>
                    <Navigation className="w-4 h-4 mr-2" />
                    Suivre la livraison
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Dialog de détails de l'offre */}
        <Dialog open={offerDetailDialogOpen} onOpenChange={setOfferDetailDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Détails de l'offre</DialogTitle>
              <DialogDescription>
                Informations complètes sur l'offre du transporteur
              </DialogDescription>
            </DialogHeader>
            {selectedOffer && (() => {
              const offerRequest = selectedOffer.request_detail || selectedOffer.request || request;
              const offerVehicle = selectedOffer.vehicle_detail || selectedOffer.vehicle;
              return (
                <div className="space-y-6">
                  {/* Informations de l'offre */}
                  <div>
                    <h3 className="font-semibold mb-3">Informations de l'offre</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Prix de l'offre</Label>
                        <p className="text-2xl font-bold text-secondary mt-1">
                          {formatCurrency(selectedOffer.admin_display_price || selectedOffer.price)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Statut</Label>
                        <div className="mt-1">{getOfferStatusBadge(selectedOffer.status)}</div>
                      </div>
                      {selectedOffer.estimated_distance_km && (
                        <div>
                          <Label className="text-muted-foreground">Distance estimée</Label>
                          <p className="font-medium mt-1">{selectedOffer.estimated_distance_km} km</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Date de réception</Label>
                        <p className="font-medium mt-1">{formatDate(selectedOffer.created_at)}</p>
                      </div>
                    </div>
                    {selectedOffer.message && (
                      <div className="mt-4">
                        <Label className="text-muted-foreground">Message du transporteur</Label>
                        <div className="mt-2 p-3 bg-primary-700/30 rounded-lg">
                          <p className="text-sm whitespace-pre-wrap">{selectedOffer.message}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Informations du trajet */}
                  {offerRequest && (
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Trajet
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Départ</Label>
                          <p className="font-medium mt-1">
                            {offerRequest.from_location_detail?.city ||
                             offerRequest.from_location_detail?.label ||
                             offerRequest.from_location_detail?.name ||
                             offerRequest.from_location?.city ||
                             offerRequest.from_location?.label ||
                             offerRequest.from_location?.name ||
                             'N/A'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Destination</Label>
                          <p className="font-medium mt-1">
                            {offerRequest.to_location_detail?.city ||
                             offerRequest.to_location_detail?.label ||
                             offerRequest.to_location_detail?.name ||
                             offerRequest.to_location?.city ||
                             offerRequest.to_location?.label ||
                             offerRequest.to_location?.name ||
                             'N/A'}
                          </p>
                        </div>
                        {offerRequest.preferred_pickup_date && (
                          <div>
                            <Label className="text-muted-foreground">Date de ramassage</Label>
                            <p className="font-medium mt-1">{formatDateOnly(offerRequest.preferred_pickup_date)}</p>
                          </div>
                        )}
                        {offerRequest.preferred_delivery_date && (
                          <div>
                            <Label className="text-muted-foreground">Date de livraison</Label>
                            <p className="font-medium mt-1">{formatDateOnly(offerRequest.preferred_delivery_date)}</p>
                          </div>
                        )}
                        {offerRequest.payment_terms && (
                          <div>
                            <Label className="text-muted-foreground">Modalités de paiement</Label>
                            <div className="mt-1">{getPaymentTermsBadge(offerRequest.payment_terms)}</div>
                          </div>
                        )}
                        {offerRequest.urgency_level && (
                          <div>
                            <Label className="text-muted-foreground">Niveau d'urgence</Label>
                            <div className="mt-1">{getUrgencyBadge(offerRequest.urgency_level)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Informations du transporteur */}
                  {selectedOffer.transporter_detail && (
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Truck className="w-5 h-5" />
                        Transporteur
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Nom</Label>
                          <p className="font-medium mt-1">
                            {selectedOffer.transporter_detail.organisation_name ||
                             `${selectedOffer.transporter_detail.user_detail?.first_name || ''} ${selectedOffer.transporter_detail.user_detail?.last_name || ''}`.trim() ||
                             'N/A'}
                          </p>
                        </div>
                        {selectedOffer.transporter_detail.user_detail?.email && (
                          <div>
                            <Label className="text-muted-foreground">Email</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Mail className="w-4 h-4" />
                              <p className="font-medium">{selectedOffer.transporter_detail.user_detail.email}</p>
                            </div>
                          </div>
                        )}
                        {selectedOffer.transporter_detail.user_detail?.phone_number && (
                          <div>
                            <Label className="text-muted-foreground">Téléphone</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Phone className="w-4 h-4" />
                              <p className="font-medium">{selectedOffer.transporter_detail.user_detail.phone_number}</p>
                            </div>
                          </div>
                        )}
                        {selectedOffer.transporter_detail.rating_avg && (
                          <div>
                            <Label className="text-muted-foreground">Note moyenne</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              <p className="font-medium">{selectedOffer.transporter_detail.rating_avg}/5</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Informations du véhicule */}
                  {offerVehicle && (
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Truck className="w-5 h-5" />
                        Véhicule
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Plaque d'immatriculation</Label>
                          <p className="font-medium mt-1">{offerVehicle.plate_number || 'N/A'}</p>
                        </div>
                        {(offerVehicle.vehicle_type_label || offerVehicle.vehicle_type?.label || offerVehicle.vehicle_type) && (
                          <div>
                            <Label className="text-muted-foreground">Type de véhicule</Label>
                            <p className="font-medium mt-1">
                              {offerVehicle.vehicle_type_label || offerVehicle.vehicle_type?.label || offerVehicle.vehicle_type || 'N/A'}
                            </p>
                          </div>
                        )}
                        {offerVehicle.make && offerVehicle.model && (
                          <div>
                            <Label className="text-muted-foreground">Marque / Modèle</Label>
                            <p className="font-medium mt-1">
                              {offerVehicle.make} {offerVehicle.model}
                              {offerVehicle.year && ` (${offerVehicle.year})`}
                            </p>
                          </div>
                        )}
                        {(offerVehicle.capacity_weight || offerVehicle.capacity_volume) && (
                          <div>
                            <Label className="text-muted-foreground">Capacité</Label>
                            <p className="font-medium mt-1">
                              {offerVehicle.capacity_weight && `${offerVehicle.capacity_weight} kg`}
                              {offerVehicle.capacity_weight && offerVehicle.capacity_volume && ' / '}
                              {offerVehicle.capacity_volume && `${offerVehicle.capacity_volume} m³`}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOfferDetailDialogOpen(false)}>
                Fermer
              </Button>
              {selectedOffer?.status === 'PENDING' && (
                <Button
                  onClick={() => {
                    setOfferDetailDialogOpen(false);
                    handleAcceptOffer(selectedOffer);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Valider l'offre
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de validation d'offre avec montant final */}
        <AlertDialog open={acceptOfferDialogOpen} onOpenChange={setAcceptOfferDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Valider l'offre</AlertDialogTitle>
              <AlertDialogDescription>
                Vous êtes sur le point d'accepter l'offre du transporteur {selectedOffer?.transporter?.organisation_name}.
                Veuillez confirmer le montant final convenu pour le transport.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Montant proposé par le transporteur</Label>
                <p className="text-xl font-bold text-secondary mt-1">
                  {selectedOffer ? formatCurrency(selectedOffer.price) : 'N/A'}
                </p>
              </div>
              <div>
                <Label htmlFor="finalAmount">Montant final convenu (GNF) *</Label>
                <Input
                  id="finalAmount"
                  type="number"
                  placeholder="Entrez le montant final"
                  value={finalAmount}
                  onChange={(e) => setFinalAmount(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Ce montant sera utilisé pour la réservation et les factures.
                </p>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmAcceptOffer}
                className="bg-green-600 hover:bg-green-700"
                disabled={!finalAmount || parseFloat(finalAmount) <= 0}
              >
                Confirmer et valider
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DashboardLayout>
    </>
  );
};

export default ClientRequestDetails;
